import clx from 'classnames';
import { useState, useEffect, useRef } from 'react';
import { Tooltip } from 'reactstrap';
import DateFormater from 'src/Utils/DateFormater';
import {
	SignerCertInfoType,
	SignatureStatuses,
} from 'src/Types/File/Attachment';
import { ReactComponent as SignCancelIcon } from './Assets/SignCancel.svg';
import { ReactComponent as SignReadyIcon } from './Assets/SignReady.svg';
import { ReactComponent as SignWaitingIcon } from './Assets/SignWaiting.svg';
import { ReactComponent as AlertIcon } from './Assets/Alert.svg';
import { ReactComponent as DownloadIcon } from 'src/Themes/assets/download.svg';
import st from './SignInfo.module.scss';

interface ICertificateInfoProps {
	targetId: string;
	status: number;
	info?: SignerCertInfoType;
	downloadCertLink?: string;
}

const CertificateInfo: React.FC<ICertificateInfoProps> = ({
	targetId,
	status,
	info,
	downloadCertLink,
}) => {
	const [tooltipOpen, setTooltipOpen] = useState(false);
	const hideTimeout = useRef<NodeJS.Timeout>();

	const isHoverable = status !== 2;

	const handleMouseEnter = () => {
		clearTimeout(hideTimeout.current);
		setTooltipOpen(true);
	};

	const handleMouseLeave = () => {
		hideTimeout.current = setTimeout(() => {
			setTooltipOpen(false);
		}, 200);
	};

	useEffect(() => {
		return () => {
			clearTimeout(hideTimeout.current);
		};
	}, []);

	const downloadFile = (e: React.MouseEvent<SVGSVGElement>) => {
		e.stopPropagation();
		if (downloadCertLink) {
			window.location.href = window.host + downloadCertLink;
		}
	};

	const tooltipId = 'cert-' + targetId;

	return (
		<>
			<span
				id={tooltipId}
				onMouseEnter={isHoverable ? handleMouseEnter : undefined}
				onMouseLeave={isHoverable ? handleMouseLeave : undefined}
				className={clx(st.iconWrapper, { [st.hover]: tooltipOpen })}
			>
				{renderSignIcon(status)}
			</span>
			{isHoverable && !!info && status !== SignatureStatuses.NotSet && (
				<Tooltip
					isOpen={tooltipOpen}
					className={st.signInfoWrapper}
					placement="bottom-start"
					target={tooltipId}
					autohide={false}
					offset={[-12, 5]}
					toggle={() => {
						return setTooltipOpen(!tooltipOpen);
					}}
				>
					<div
						onMouseEnter={handleMouseEnter}
						onMouseLeave={handleMouseLeave}
						className={st.signInfo}
					>
						{status === SignatureStatuses.Valid ? (
							<>
								<div className={st.signInfoHeader}>
									<h5
										className={clx(
											'body-large',
											st.singInfoDownload,
										)}
									>
										Сведения о сертификате
									</h5>
									{downloadCertLink && (
										<DownloadIcon
											onClick={downloadFile}
											className={st.downloadIcon}
										/>
									)}
								</div>
								{info.subjUserName && (
									<p>
										<span>Владелец:</span>
										{info.subjUserName}
									</p>
								)}
								{info.organization && (
									<p>
										<span>Компания:</span>
										{info.organization}
									</p>
								)}
								{info.position && (
									<p>
										<span>Должность:</span>
										{info.position}
									</p>
								)}
								{info.issuerUserName && (
									<p>
										<span>Издатель:</span>
										{info.issuerUserName}
									</p>
								)}
								<p>
									<span>Срок действия сертификата:</span>C{' '}
									{DateFormater.toShortString(
										info.createDateTime,
									)}{' '}
									по{' '}
									{DateFormater.toShortString(
										info.validUntilDateTime,
									)}
								</p>
								{info.serialNumber && (
									<p>
										<span>Серийный номер:</span>
										{info.serialNumber}
									</p>
								)}
								{info.thumbprint && (
									<p>
										<span>Отпечаток:</span>
										{info.thumbprint}
									</p>
								)}
							</>
						) : (
							<p className={clx(st.signInfoTitle, st.Invalid)}>
								Подпись НЕ корректна
							</p>
						)}
					</div>
				</Tooltip>
			)}
		</>
	);
};

const renderSignIcon = (status: number) => {
	switch (status) {
		case 0:
			return <SignCancelIcon />;
		case 1:
			return <SignReadyIcon />;
		case 2:
			return <SignWaitingIcon />;
		default:
			return <AlertIcon />;
	}
};

export default CertificateInfo;
